import { ChakraProvider, HStack, Stack, useDisclosure } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import Header from '../components/Header'
import { addDays, format, parse, subDays } from 'date-fns'
import Sidebar from '../components/Sidebar'
import Orders from '../components/Orders'
import '../App.css'
import Api from '../api/api'
import OrderEditModal from '../components/OrderEditModal'
import SettingsModal from '../components/SettingsModal'
import AddNewOrder from '../components/AddNewOrder'
import theme from '../theme'
import ActionPopup from '../components/ActionPopup'
import CallWaiterButton from '../components/CallWaiterButton'

function App() {
  const [today, setToday] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [date, setDate] = useState<string | null>(null)
  const [orders, setOrders] = useState<Array<any> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dates, setDates] = useState<Array<any> | null>(null)
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timer | null>(null)
  const [orderCountInterval, setOrderCountInterval] = useState<NodeJS.Timer | null>(null)
  const [editingOrder, setEditingOrder] = useState(null)
  const [lastUpdate, setLastUpdate] = useState(new Date())
  const [orderCounter, setOrderCounter] = useState<Array<any> | null>(null)
  const [locations, setLocations] = useState<Array<string>>([])
  const [menu, setMenu] = useState<Array<string>>([])
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [teraceEnabled, setTeraceEnabled] = useState(false)
  const [roomEnabled, setRoomEnabled] = useState(false)
  const [lobbyEnabled, setLobbyEnabled] = useState(false)
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedDates, setSelectedDates] = useState<Date[] | string[]>([])
  const [showCompletedOrders, setShowCompletedOrders] = useState(
    localStorage.getItem('kitchen-show-completed-orders') === 'true',
  )
  const [action, setAction] = useState(null)
  const [workendAction, setWorkendAction] = useState(null)
  const prevOrdersCount = useRef(0);

  useEffect(() => {
    let intervalId: NodeJS.Timer | null = null

    const fetchActions = async () => {
      return Api.getPendingAction().then((response) => {
        if (response.actions) {
          const instantActions = response.actions.filter((action: any) => action.type === 'time')
          if (instantActions.length > 0) {
            setAction(instantActions[0])
          }
          const workendActions = response.actions.filter(
            (action: any) => action.type === 'work-end',
          )

          if (workendAction === null && workendActions.length > 0) {
            setWorkendAction(workendActions[0])
          }
        }
      })
    }

    if (!action) {
      intervalId = setInterval(() => {
        fetchActions()
      }, 3000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [action, workendAction])

  const dateGenerator = () => {
    const dateGenerator = []
    let dateCounter = new Date()
    for (let i = 0; i < 5; i++) {
      const active = i === 0
      const name = format(dateCounter, 'EEEE')
      const date = format(dateCounter, 'yyyy-MM-dd')
      dateGenerator.push({
        name: name,
        date: date,
        active: active,
      })
      if (active) {
        setDate(date)
        const dateObj = parse(date, 'yyyy-MM-dd', new Date())
        setSelectedDates([dateObj, dateObj])
      }
      dateCounter = addDays(dateCounter, 1)
    }
    return dateGenerator
  }

  useEffect(() => {
    if (!orders) {
      return;
    }
    if (prevOrdersCount.current < orders.length) {
      const audio = new Audio('/assets/new-order.mp3')
      audio.play().catch((err) => console.error('Audio play failed:', err))
    }
    prevOrdersCount.current = orders.length
  }, [orders]);

  const getOrders = () => {
    Api.getOrders(date)
      .then((result) => {
        setLastUpdate(new Date())

        setOrders(result.sort((order: any) => order.status === 'created'))
      })
      .catch(() => {
        setError('Error on fetching orders')
      })
      .finally(() => {
        setOrdersLoading(false)
      })
  }

  const getOrderCount = () => {
    if (dates) {
      Api.getOrderCount(dates).then((result) => {
        setOrderCounter(result)
      })
    }
  }

  const getLocations = () => {
    Api.getLocations()
      .then((result) => {
        setLocations(result)
        const teraceLocation = result.find(
          (location: any) => location.location === 'terace' && location.type === 'client',
        )
        if (teraceLocation) {
          setTeraceEnabled(teraceLocation.enabled || false)
        }
        const lobbyLocation = result.find(
          (location: any) =>
            location.location === 'at the lobby restaurant' && location.type === 'client',
        )
        if (lobbyLocation) {
          setLobbyEnabled(lobbyLocation.enabled || false)
        }
        const roomLocation = result.find(
          (location: any) => location.location === 'to the room' && location.type === 'client',
        )
        if (roomLocation) {
          setRoomEnabled(roomLocation.enabled || false)
        }
      })
      .catch(() => {
        setError('Error on fetching locations')
      })
  }

  const getMenu = () => {
    Api.getMenu()
      .then((result) => {
        setMenu(result)
      })
      .catch(() => {
        setError('Error on fetching menu')
      })
  }

  useEffect(() => {
    setDates(dateGenerator())
    getLocations()
    getMenu()
  }, [])

  useEffect(() => {
    if (date) {
      setOrdersLoading(true)
      getOrders()
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
      setRefreshInterval(setInterval(getOrders, 10000))
    }
  }, [date])

  useEffect(() => {
    setTimeout(() => {
      getOrderCount()
    }, 1000)
    if (orderCountInterval) {
      clearInterval(orderCountInterval)
    }
    setOrderCountInterval(setInterval(getOrderCount, 30 * 1000))
  }, [dates])

  const handleDateSelect = (date: string) => {
    if (dates) {
      setDate(date)

      const dateObj = parse(date, 'yyyy-MM-dd', new Date())
      setSelectedDates([dateObj, dateObj])
      const dateIndex = dates.findIndex((dateInfo) => {
        return date === dateInfo.date
      })
      dates.forEach((date) => (date.active = false))
      if (dateIndex !== -1) {
        dates[dateIndex].active = true
      }
    }
  }

  const handleOrderComplete = (order: any, status: string, changeCashRegisterSend: boolean|null) => {
    if (order.id) {
        setLoading(true)
        Api
            .updateOrderStatus(order.id, status, changeCashRegisterSend)
            .then(() => {
                getOrders()
                setLoading(false)
            })
    }
  }

  const addDate = (forward = true) => {
    if (dates) {
      if (forward) {
        const lastDay = addDays(parse(dates[dates.length - 1].date, 'yyyy-MM-dd', new Date()), 1)
        const name = format(lastDay, 'EEEE')
        const date = format(lastDay, 'yyyy-MM-dd')
        const dateToPush = {
          name: name,
          date: date,
          active: false,
        }
        setDates((oldDates) => [...(oldDates ?? []), dateToPush])
      } else {
        const firstDate = subDays(parse(dates[0].date, 'yyyy-MM-dd', new Date()), 1)
        const name = format(firstDate, 'EEEE')
        const date = format(firstDate, 'yyyy-MM-dd')
        const dateToPush = {
          name: name,
          date: date,
          active: false,
        }
        setDates((oldDates) => [dateToPush, ...(oldDates ?? [])])
      }
    }
  }

  const handleOrderEdit = (order: any) => {
    setEditingOrder(order)
  }

  const handleOrderNotify = (order: any) => {
    Api.updateOrderNotify(order).then(() => {
        getOrders()
    })
  }

  const handleOrderEditClose = (order: any) => {
    setEditingOrder(null)
  }

  const handleOrderSave = (order: any) => {
    Api.updateOrder(order)
    setEditingOrder(null)
  }

  const handleOrderUpdate = (order: any, param: string, value: string) => {
    setOrders(
      orders?.map((o) => {
        if (o.id === order.id) {
          return { ...o, [param]: value }
        }

        return o
      }) ?? [],
    )
    order[param] = value
  }

  const handleShowSettingsModal = (show = false) => {
    setShowSettingsModal(show)
  }

  const handleTeraceChange = (change: any) => {
    const checked = change.target.checked
    setTeraceEnabled(checked)

    const teraceLocation: any = locations.find((location: any) => location.location === 'terace')
    if (teraceLocation) {
      Api.updateLocation({ id: teraceLocation.id, enabled: checked })
    }
  }

  const handleRoomChange = (change: any) => {
    const checked = change.target.checked
    setRoomEnabled(checked)

    const roomLocation: any = locations.find((location: any) => location.location === 'to the room')
    if (roomLocation) {
      Api.updateLocation({ id: roomLocation.id, enabled: checked })
    }
  }

  const handlelobbyChange = (change: any) => {
    const checked = change.target.checked
    setLobbyEnabled(checked)

    const lobbyLocation: any = locations.find(
      (location: any) => location.location === 'at the lobby restaurant',
    )
    if (lobbyLocation) {
      Api.updateLocation({ id: lobbyLocation.id, enabled: checked })
    }
  }

  const handleShowCompletedOrdersChange = (change: any) => {
    setShowCompletedOrders(change.target.checked)
    localStorage.setItem('kitchen-show-completed-orders', change.target.checked)
  }

  const handleDateRangeSelect = (dates: any) => {
    setSelectedDates(dates)
    if (dates.length === 2) {
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }

      setOrdersLoading(true)

      Api.getOrdersByRange(dates)
        .then((result) => {
          setOrders(result.sort((order: any) => order.status === 'created'))
        })
        .finally(() => setOrdersLoading(false))
    }
  }

  const handleOrderDelete = (order: any) => {
    const confirmed = confirm('Are you sure you want to delete order?')
    if (confirmed) {
      Api.deleteOrder(order)
      getOrders()
      setEditingOrder(null)
    }
  }

  const handleOrderDrinksStart = (order: any) => {
      Api.orderDrinksStart(order).then(() => {
        getOrders()
      })
  }

  const onOrderCreated = () => {
    getOrders()
  }

  return (
    <ChakraProvider theme={theme}>
      {action && action.id && <ActionPopup action={action} setAction={setAction}></ActionPopup>}
      <Header
        lastUpdate={lastUpdate}
        onShowSettingsModal={() => handleShowSettingsModal(true)}
        selectedDates={selectedDates}
        onSelectedDates={handleDateRangeSelect}
        workendAction={workendAction}
        setWorkendAction={setWorkendAction}
      />
      <Stack>
        <Sidebar
          dates={dates}
          onDaySelect={handleDateSelect}
          addDate={addDate}
          // @ts-ignore
          orderCounter={orderCounter && orderCounter['orders'] ? orderCounter['orders'] : []}
          // @ts-ignore
          reservationsCounter={
            orderCounter && orderCounter['reservations'] ? orderCounter['reservations'] : []
          }
        />
        <Orders
          loading={loading}
          orders={orders}
          locations={locations}
          menu={menu}
          onOrderComplete={handleOrderComplete}
          onOrderEdit={handleOrderEdit}
          onOrderNotify={handleOrderNotify}
          onOrderDrinksStart={handleOrderDrinksStart}
          ordersLoading={ordersLoading}
          showCompletedOrders={showCompletedOrders}
        />
      </Stack>
      <OrderEditModal
        order={editingOrder}
        onClose={handleOrderEditClose}
        onOrderUpdate={handleOrderUpdate}
        onOrderSave={handleOrderSave}
        onOrderDelete={handleOrderDelete}
      />
      <SettingsModal
        show={showSettingsModal}
        onClose={() => handleShowSettingsModal()}
        teraceEnabled={teraceEnabled}
        onTeraceChange={handleTeraceChange}
        roomEnabled={roomEnabled}
        onRoomChange={handleRoomChange}
        lobbyEnabled={lobbyEnabled}
        onLobbyChange={handlelobbyChange}
        showCompletedOrders={showCompletedOrders}
        onShowCompletedOrdersChange={handleShowCompletedOrdersChange}
      />
      <CallWaiterButton />
    </ChakraProvider>
  )
}

export default App
